// R1-VISUAL Slice 4 — About the Owner. Biography paragraphs pasted VERBATIM
// per Edwin's directive. Any change to the 8 paragraphs, the pull-quote, or
// the timeline/highlight strings must go through a compliance review because
// they are the source of truth for Twilio Trust Hub, HICPA, and any future
// public-record request.
//
// R1-VISUAL Slice 2/3 addendum — body is now exported separately as
// <AboutOwnerBody /> so the public landing page can embed the founder story
// at Section 6 without nesting <PublicShell>.
import React from "react";
import { Link } from "react-router-dom";
import PublicShell from "../../components/public/PublicShell";

const PORTRAIT = "/brand/edwin-flamer-founder-portrait-v2.jpg";
const PORTRAIT_ALT = "Edwin R. Flamer, Founder and Owner of ERF ENTERPRISES LLC.";

const BIO = [
  // CORRECTIVE PASS — RESTORED to ORIGINAL 8 paragraphs (per 79d04fb) with
  // the approved father-history paragraph INSERTED at index 1 (position 2).
  // Do NOT paraphrase, shorten, or generically rewrite. This is the source
  // of truth for Twilio Trust Hub, HICPA, and any future public-record
  // request. Corrective pass supersedes the consolidated 9-paragraph rewrite.
  //
  // Attribution boundaries (must remain true):
  //  - "More than 35 years" is Edwin R. Flamer's PERSONAL experience.
  //  - Ambassador Realty Group + ARG Property Management were owned/operated
  //    by Edwin's FATHER — never divisions of ERF.
  //  - EF1 General Contracting dates to 2001. F Squared LLC is part of
  //    Edwin's prior independent business experience.
  //  - ERF ENTERPRISES LLC was founded in 2021.
  //  - Lukens Steel Mill, Steelworkers Local 15253, Toll Brothers and
  //    Orleans Homebuilders are HISTORICAL professional experience only.
  "Edwin R. Flamer brings more than 35 years of experience across construction, industrial operations and project leadership. His career was built from the field upward, giving him a practical understanding of craftsmanship, production expectations, jobsite coordination, safety, accountability and the decisions required to move complex work forward.",
  "Edwin's foundation began at approximately 10 years old while learning and working alongside his father, who owned and operated Ambassador Realty Group and ARG Property Management. That early exposure introduced him to construction, property operations, maintenance, project evaluation, responsibility, customer service, and the importance of protecting an owner's investment. It also established the multigenerational work ethic and business foundation that continue to influence his leadership today.",
  "His early career foundation included industrial experience at Lukens Steel Mill and union heavy-highway construction experience with Local Steel Workers Union Heavy Highway and Construction 15253. As a bridge carpenter, Edwin worked in large-scale infrastructure construction, reinforcing the importance of precision, structural discipline, teamwork, safety and dependable execution.",
  "Edwin expanded that foundation through work connected with respected residential builders Toll Brothers and Orleans Homebuilders. Exposure to major homebuilding operations strengthened his understanding of production construction, scheduling, trade coordination, quality expectations and the many decisions required to move a home from planning through completion.",
  "Beginning in 2001, Edwin moved into construction-business ownership and operated EF1 General Contracting and F Squared LLC. Through those companies, he developed extensive experience across all phases of residential and commercial construction, including project evaluation, estimating, scope development, jobsite supervision, subcontractor coordination, material and labor planning, quality control, problem-solving and client communication.",
  "His construction background includes residential and commercial renovations, rehabilitation, roofing and waterproofing, demolition and structural work, concrete and excavation, interior and exterior improvements, property-related construction services, and coordination across carpentry, electrical, plumbing, HVAC and finish trades. His responsibilities have included permitting and inspection coordination, workforce and subcontractor management, schedule decisions, cost awareness and maintaining accountability among owners, vendors and project participants.",
  "In 2021, Edwin established ERF ENTERPRISES LLC to bring that accumulated experience into a stronger owner-led general-contracting operation. ERF combines field knowledge, executive judgment and modern project controls to serve residential and commercial clients with disciplined planning, transparent communication and direct accountability.",
  "Edwin remains personally involved in project planning and oversight. His leadership is informed by experience across industrial work, heavy-highway infrastructure, production homebuilding and independent general contracting. That range allows him to evaluate work from multiple perspectives: the craftsperson performing it, the superintendent coordinating it, the contractor responsible for it and the owner investing in the result.",
  "For Edwin, construction is not simply completing a scope. It is protecting the client's investment, anticipating problems, coordinating people responsibly and delivering work that everyone involved is willing to put their name behind.",
];

const TIMELINE = [
  // CORRECTIVE PASS — 6 entries. Approved "Family Foundation" inserted at
  // position 01. Original 5 entries retained VERBATIM at positions 02-06.
  // F Squared LLC is the correct entity name per Owner confirmation.
  { n: "01", title: "Family Foundation",                copy: "Beginning at approximately 10 years old, Edwin learned construction, property operations, maintenance, customer service, and business responsibility while working alongside his father through Ambassador Realty Group and ARG Property Management." },
  { n: "02", title: "Industrial & Union Foundation",    copy: "Lukens Steel Mill and Local Steel Workers Union Heavy Highway and Construction 15253 experience." },
  { n: "03", title: "Infrastructure Craftsmanship",     copy: "Heavy-highway bridge carpentry and structural field discipline." },
  { n: "04", title: "Production Homebuilding",          copy: "Experience connected with Toll Brothers and Orleans Homebuilders." },
  { n: "05", title: "Business Ownership Since 2001",    copy: "EF1 General Contracting and F Squared LLC." },
  { n: "06", title: "ERF ENTERPRISES LLC Since 2021",   copy: "Owner-led residential and commercial general contracting." },
];

const HIGHLIGHTS = [
  // CORRECTIVE PASS — restored to original 4-item list per 79d04fb.
  { h: "35+ Years of Experience",                                     s: "Construction, industrial operations and project leadership." },
  { h: "Business Leadership Since 2001",                              s: "EF1 General Contracting and F Squared LLC." },
  { h: "Residential, Commercial & Infrastructure Perspective",        s: "Experience ranging from homebuilding to bridge carpentry and multi-trade construction." },
  { h: "Owner-Led Decisions",                                         s: "Planning, estimating, coordination, supervision, quality control and client accountability." },
];

// Body-only export — safe to embed inside another <PublicShell>-hosted page.
export function AboutOwnerBody({ compact = false }) {
  return (
    <section className="erf-public-section" data-testid="about-owner-body" style={{ background: compact ? "transparent" : undefined }}>
      <div className="erf-public-container" style={{ maxWidth: 1200 }}>
        <div className="erf-public-eyebrow" style={{ letterSpacing: "0.28em", color: "#B08D57" }}>MEET THE FOUNDER</div>
        <h1 style={{ marginTop: 12, fontFamily: '"Playfair Display", Georgia, serif' }}>
          Experience Built in the Field. Leadership Proven Over Time.
        </h1>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 420px)", gap: 48, marginTop: 40, alignItems: "start" }} className="erf-about-grid">
          <div style={{ maxWidth: "62ch" }}>
            <div style={{ borderLeft: "3px solid #B08D57", paddingLeft: 20, marginBottom: 32 }}>
              <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 22, fontWeight: 600, color: "#0B2545" }}>Edwin R. Flamer</div>
              <div style={{ fontSize: 14, color: "#1F2937", marginTop: 4 }}>Founder, Owner &amp; Project Manager</div>
              <div style={{ fontSize: 13, color: "#1F2937", opacity: 0.8, marginTop: 2 }}>ERF ENTERPRISES LLC</div>
            </div>

            {BIO.map((p, i) => (
              <p key={i} data-testid={`about-owner-bio-p${i + 1}`} style={{ fontSize: 16, lineHeight: 1.75, color: "#1F2937", marginBottom: 18 }}>
                {p}
              </p>
            ))}

            <blockquote
              data-testid="about-owner-pullquote"
              style={{
                borderLeft: "3px solid #B08D57",
                paddingLeft: 20,
                margin: "32px 0",
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: "italic",
                fontSize: 20,
                lineHeight: 1.55,
                color: "#5B3A1F",
              }}
            >
              "Every project deserves disciplined planning, honest communication and work we are willing to put our name behind."
              <footer style={{ marginTop: 10, fontSize: 13, fontStyle: "normal", color: "#1F2937", opacity: 0.75 }}>
                — Edwin R. Flamer, Founder
              </footer>
            </blockquote>
          </div>

          <picture>
            <img
              src={PORTRAIT}
              alt={PORTRAIT_ALT}
              data-testid="about-owner-portrait"
              loading="lazy"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: 6,
                border: "1px solid #E5E1DA",
                boxShadow: "0 12px 32px -12px rgba(11,37,69,0.35)",
                display: "block",
              }}
            />
          </picture>
        </div>

        <div style={{ marginTop: 64 }}>
          <div className="erf-public-eyebrow" style={{ letterSpacing: "0.28em", color: "#B08D57" }}>EXPERIENCE TIMELINE</div>
          <div style={{ marginTop: 20, borderTop: "1px solid #E5E1DA" }} data-testid="about-owner-timeline">
            {TIMELINE.map((t, i) => (
              <div
                key={t.title}
                data-testid={`about-owner-timeline-row-${i + 1}`}
                style={{ display: "grid", gridTemplateColumns: "48px 1fr", gap: 20, padding: "20px 0", borderBottom: "1px solid #E5E1DA", alignItems: "start" }}
              >
                <div style={{ fontFamily: '"IBM Plex Mono", monospace', color: "#B08D57", fontSize: 13, letterSpacing: "0.1em" }}>{String(i + 1).padStart(2, "0")}</div>
                <div>
                  <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 18, fontWeight: 600, color: "#0B2545" }}>{t.title}</div>
                  <div style={{ fontSize: 14, color: "#1F2937", marginTop: 4, opacity: 0.85 }}>{t.copy}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 64 }} data-testid="about-owner-highlights">
          <div className="erf-public-eyebrow" style={{ letterSpacing: "0.28em", color: "#B08D57" }}>WHAT EDWIN BRINGS</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 20,
              marginTop: 20,
            }}
          >
            {HIGHLIGHTS.map((h, i) => (
              <div
                key={h.h}
                data-testid={`about-owner-highlight-${i + 1}`}
                style={{
                  border: "1px solid #B08D57",
                  background: "#F5F1E8",
                  padding: "20px 20px 18px",
                  borderRadius: 4,
                  boxShadow: "inset 0 0 0 1px rgba(176,141,87,0.15)",
                }}
              >
                <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 17, fontWeight: 600, color: "#0B2545", lineHeight: 1.3 }}>{h.h}</div>
                <div style={{ fontSize: 13, color: "#1F2937", marginTop: 8, lineHeight: 1.55 }}>{h.s}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 56, padding: "28px 24px", background: "#0B2545", color: "#F5F1E8", borderRadius: 4, textAlign: "center" }}>
          <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 22, marginBottom: 12 }}>Ready to plan the next build?</div>
          <Link
            to="/quote"
            data-testid="about-owner-cta-quote"
            style={{ display: "inline-block", padding: "12px 28px", background: "#B08D57", color: "#0B2545", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 13, borderRadius: 3 }}
          >
            Request a Quote
          </Link>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .erf-about-grid { grid-template-columns: 1fr !important; }
          .erf-about-grid > picture { order: -1; max-width: 360px; margin: 0 auto; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="about-owner-portrait"] { transition: none !important; animation: none !important; }
        }
      `}</style>
    </section>
  );
}

export default function AboutOwner() {
  return (
    <PublicShell>
      <AboutOwnerBody />
    </PublicShell>
  );
}
